import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, FormControlLabel, MenuItem, Select, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import { FieldBinding, ObjectLocks, SlideObject, resolvedLineHeight } from '../types';
import { catalogFieldOptions, fieldLabel, LogicalField, TemplateFieldOption } from '../fieldCatalog';
import { usePresentationSettings } from '../PresentationSettingsContext';
import { settingsLogicalFields } from '../presentationSettings';
import { listDamTemplates } from '../api';
import { applyListHtml, detectListKind } from './listHtml';
import {
  isToolbarPointerTarget,
  LeadingLooseIcon,
  LeadingTightIcon,
  SliderRow,
  TrackingLooseIcon,
  TrackingTightIcon,
} from './ToolbarFlyout';
import { PRES, TYPE } from '../chrome';
import ColorField from './ColorField';
import EffectsControls from './EffectsControls';
import SteppedNumberField from './SteppedNumberField';
import TypefaceSelect from './TypefaceSelect';
import { TextStyleStep } from './layerList';
import TextStyleSelect from './TextStyleSelect';
import { LineDashToggle, TextAlignToggle, VerticalAlignToggle, toolbarToggleSx } from './toolbarIcons';
import { COPY } from '../presentationCopy';
import { PanelRow, PanelSection } from '../ui';
import { hasShapeMedia, isFillableShape } from './shapeFrame';
import { clearedShapeMedia } from './placeTarget';
import { TABLE_FONT_SIZE, TABLE_GRID, TABLE_HEADER_BG, TABLE_HEADER_COLOR } from './tableModel';
import { normalizeChart } from './chartModel';
import ChartDataSheet, { ChartLookControls } from './ChartDataSheet';
import { resolveBoundField } from './linkedFields';
import ColorPopoverButton from './ColorPopoverButton';

interface PropertiesPanelProps {
  object: SlideObject | null;
  advancedMode: boolean;
  embedded?: boolean;
  slideBackground?: string;
  onChange: (patch: Partial<SlideObject>) => void;
  onStyle?: (patch: Record<string, unknown>) => void;
  onSlideBackground?: (color: string) => void;
  onOpenAssets: () => void;
  onToggleList?: (kind: 'bullet' | 'ordered' | 'none') => void;
  onApplyTextStyle?: (step: TextStyleStep) => void;
  onSetLinkedField?: (key: string, source: 'direct' | 'local') => void;
  slideObjects?: SlideObject[];
  onDetachGlobal?: () => void;
  canDetachGlobal?: boolean;
  templateMode?: boolean;
  onFieldBinding?: (binding: FieldBinding | undefined, placeholder?: string) => void;
}

const objectTitle = (object: SlideObject): string => {
  if (object.type === 'text') return 'Text';
  if (object.type === 'product') return 'Produkt';
  if (object.type === 'image') return 'Bild';
  if (object.type === 'table') return 'Tabell';
  if (object.type === 'chart') return COPY.chart;
  if (object.type === 'shape' && hasShapeMedia(object)) return 'Bild i form';
  if (object.type === 'shape' && object.style?.shape === 'line') return 'Linje';
  return 'Form';
};

function placeholderForBinding(
  binding: FieldBinding,
  productFields: TemplateFieldOption[],
  wineFields: TemplateFieldOption[],
  existingContent?: string,
  fields?: LogicalField[],
): string {
  const label = fieldLabel(binding.key, [...productFields, ...wineFields], fields);
  const kind = detectListKind(existingContent);
  return kind === 'none' ? label : applyListHtml(label, kind);
}

function FieldBindingControls({
  binding,
  content,
  productFields,
  wineFields,
  logicalFields,
  onFieldBinding,
}: {
  binding?: FieldBinding;
  content?: string;
  productFields: TemplateFieldOption[];
  wineFields: TemplateFieldOption[];
  logicalFields: LogicalField[];
  onFieldBinding: (binding: FieldBinding | undefined, placeholder?: string) => void;
}) {
  const rawKey = binding?.kind === 'raw' ? binding.key : '';
  const productKeys = useMemo(() => new Set(productFields.map((field) => field.key)), [productFields]);
  const wineKeys = useMemo(() => new Set(wineFields.map((field) => field.key)), [wineFields]);
  const inProduct = Boolean(rawKey && productKeys.has(rawKey));
  const inWine = Boolean(rawKey && wineKeys.has(rawKey));
  const unknownRaw = Boolean(rawKey && !inProduct && !inWine);
  const productValue = inProduct || unknownRaw ? rawKey : '';
  const wineValue = inWine && !inProduct ? rawKey : '';
  const logicalValue = binding?.kind === 'logical' ? binding.key : '';

  const applyBinding = (next: FieldBinding | undefined) => {
    onFieldBinding(
      next,
      next ? placeholderForBinding(next, productFields, wineFields, content, logicalFields) : undefined,
    );
  };

  return (
    <PanelRow label={COPY.fieldBinding} stacked>
      <Select
        size="small"
        fullWidth
        value={logicalValue}
        onChange={(event) => {
          const value = String(event.target.value);
          applyBinding(value ? { kind: 'logical', key: value } : undefined);
        }}
      >
        <MenuItem value="">{COPY.fieldBindingNone}</MenuItem>
        {logicalFields.map((field) => (
          <MenuItem key={field.id} value={field.id}>{field.label}</MenuItem>
        ))}
      </Select>
      <TextField
        select
        size="small"
        fullWidth
        label={COPY.fieldBindingProduct}
        value={productValue}
        onChange={(event) => {
          const value = String(event.target.value);
          applyBinding(value ? { kind: 'raw', key: value } : undefined);
        }}
        sx={{ mt: 1.25 }}
      >
        <MenuItem value="">{COPY.fieldBindingChooseProduct}</MenuItem>
        {unknownRaw && <MenuItem value={rawKey}>{rawKey}</MenuItem>}
        {productFields.map((field) => (
          <MenuItem key={field.key} value={field.key}>{field.label}</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        fullWidth
        label={COPY.fieldBindingWine}
        value={wineValue}
        onChange={(event) => {
          const value = String(event.target.value);
          applyBinding(value ? { kind: 'raw', key: value } : undefined);
        }}
        sx={{ mt: 1.25 }}
      >
        <MenuItem value="">{COPY.fieldBindingChooseWine}</MenuItem>
        {wineFields.map((field) => (
          <MenuItem key={field.key} value={field.key}>{field.label}</MenuItem>
        ))}
      </TextField>
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mt: 0.5 }}>
        {COPY.fieldBindingHelp}
      </Typography>
    </PanelRow>
  );
}

function LinkedSourceToggle({
  source,
  onChange,
}: {
  source: 'direct' | 'local';
  onChange: (source: 'direct' | 'local') => void;
}) {
  return (
    <>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={source}
        onChange={(_event, value) => {
          if (value === 'direct' || value === 'local') onChange(value);
        }}
      >
        <ToggleButton value="direct">{COPY.linkedDirect}</ToggleButton>
        <ToggleButton value="local">{COPY.linkedLocal}</ToggleButton>
      </ToggleButtonGroup>
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mt: 0.5 }}>
        {source === 'direct' ? COPY.linkedDirectHelp : COPY.linkedLocalHelp}
      </Typography>
    </>
  );
}

function TextCaseToggle({
  value,
  disabled,
  onChange,
}: {
  value?: string;
  disabled?: boolean;
  onChange: (value: 'none' | 'uppercase' | 'lowercase') => void;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      disabled={disabled}
      value={value || 'none'}
      onChange={(_event, next) => next && onChange(next)}
      sx={toolbarToggleSx}
    >
      <ToggleButton value="none" title="Normal" aria-label="Normal">Aa</ToggleButton>
      <ToggleButton value="uppercase" title="Versaler" aria-label="Versaler">AA</ToggleButton>
      <ToggleButton value="lowercase" title="Gemener" aria-label="Gemener">aa</ToggleButton>
    </ToggleButtonGroup>
  );
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  object, advancedMode, embedded, slideBackground, onChange, onStyle, onSlideBackground, onOpenAssets, onToggleList, onApplyTextStyle,
  onSetLinkedField, slideObjects = [], onDetachGlobal, canDetachGlobal, templateMode, onFieldBinding,
}) => {
  const { settings } = usePresentationSettings();
  const logicalFields = settingsLogicalFields(settings);
  const [catalog, setCatalog] = useState<{ product: TemplateFieldOption[]; wine: TemplateFieldOption[] }>({
    product: [],
    wine: [],
  });

  useEffect(() => {
    let cancelled = false;
    listDamTemplates()
      .then((templates) => {
        if (!cancelled) setCatalog(catalogFieldOptions(templates));
      })
      .catch(() => {
        if (!cancelled) setCatalog({ product: [], wine: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const catalogLabels = useMemo(
    () => [...catalog.product, ...catalog.wine],
    [catalog.product, catalog.wine],
  );
  const boundField = object && object.type !== 'product'
    ? resolveBoundField(slideObjects, object, catalogLabels, logicalFields)
    : null;

  const shellSx = embedded
    ? { p: 2 }
    : { width: 300, bgcolor: PRES.surface, borderLeft: `1px solid ${PRES.borderSubtle}`, p: 2, overflow: 'auto' };

  if (!object) {
    return (
      <Box sx={shellSx}>
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary, mb: 0.75 }}>
          {COPY.properties}
        </Typography>
        <Typography sx={{ mb: 2, fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
          {COPY.emptySelection} {COPY.pickBackground}
        </Typography>
        {onSlideBackground && (
          <ColorField label="Sidbakgrund" value={slideBackground || '#FFFFFF'} onChange={onSlideBackground} grouped={advancedMode} />
        )}
      </Box>
    );
  }

  const locked = object.locks.style && !advancedMode;
  const contentLocked = object.locks.content && !advancedMode;
  const updateStyle = (patch: Record<string, unknown>) => {
    if (locked) return;
    if (onStyle) onStyle(patch);
    else onChange({ style: { ...object.style, ...patch } });
  };

  const updateLock = (key: keyof ObjectLocks, value: boolean) => {
    onChange({ locks: { ...object.locks, [key]: value } });
  };

  const isLine = object.type === 'shape' && object.style?.shape === 'line';
  const isShape = object.type === 'shape';
  const listKind = detectListKind(object.content);

  return (
    <Box sx={shellSx}>
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary, mb: 1.5 }}>
        {objectTitle(object)}
      </Typography>

      {!templateMode && boundField && onSetLinkedField && (
        <PanelRow label={`${COPY.boundFieldLinked} · ${boundField.label}`} stacked>
          <LinkedSourceToggle
            source={boundField.source}
            onChange={(source) => onSetLinkedField(boundField.key, source)}
          />
        </PanelRow>
      )}

      {templateMode && onFieldBinding && object.type === 'text' && (
        <FieldBindingControls
          binding={object.fieldBinding}
          content={object.content}
          productFields={catalog.product}
          wineFields={catalog.wine}
          logicalFields={logicalFields}
          onFieldBinding={onFieldBinding}
        />
      )}
      {templateMode && (object.type === 'product' || object.type === 'image') && (
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary, mb: 1.5 }}>
          {COPY.productBindingHelp}
        </Typography>
      )}

      {object.type === 'text' && (
        <Box
          onMouseDown={(event) => {
            if (isToolbarPointerTarget(event.target)) return;
            event.preventDefault();
          }}
        >
          <PanelRow label="Stil" stacked>
            <TextStyleSelect
              fullWidth
              object={object}
              disabled={locked}
              onChange={(step) => onApplyTextStyle?.(step)}
            />
          </PanelRow>
          <PanelRow label="Typsnitt" stacked>
            <TypefaceSelect
              fullWidth
              disabled={locked}
              style={object.style}
              onChange={(font) => updateStyle({ fontFamily: font.family, fontWeight: font.fontWeight })}
            />
          </PanelRow>
          <PanelRow label="Storlek">
            <SteppedNumberField
              value={object.style?.fontSize || 24}
              min={10}
              max={200}
              disabled={locked}
              width={72}
              onChange={(value) => updateStyle({ fontSize: value })}
            />
          </PanelRow>
          <PanelRow label="Lista">
            <ToggleButtonGroup
              exclusive
              size="small"
              disabled={locked}
              value={listKind}
              sx={toolbarToggleSx}
              onChange={(_event, value) => onToggleList?.(value || 'none')}
            >
              <ToggleButton value="bullet" title="Punktlista"><FormatListBulletedIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="ordered" title="Numrerad lista"><FormatListNumberedIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="none" title="Ingen lista"><FormatClearIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </PanelRow>
          <PanelRow label="Justering">
            <TextAlignToggle
              disabled={locked}
              value={object.style?.textAlign || 'left'}
              onChange={(value) => updateStyle({ textAlign: value })}
            />
          </PanelRow>
          <PanelRow label={COPY.verticalAlign}>
            <VerticalAlignToggle
              disabled={locked}
              value={object.style?.verticalAlign || 'top'}
              onChange={(value) => updateStyle({ verticalAlign: value })}
            />
          </PanelRow>
          <PanelSection title="Färger">
            <PanelRow label="Textfärg">
              <ColorPopoverButton
                label="Textfärg"
                color={object.style?.color || '#1B3967'}
                disabled={locked}
                size={22}
                onChange={(color) => updateStyle({ color })}
              />
            </PanelRow>
            <PanelRow label="Textbakgrund">
              <ColorPopoverButton
                label="Textbakgrund"
                color={object.style?.backgroundColor || 'transparent'}
                allowNone
                disabled={locked}
                size={22}
                onChange={(color) => updateStyle({ backgroundColor: color })}
              />
            </PanelRow>
          </PanelSection>
          <PanelSection title="Avancerad text" collapsible defaultOpen={false}>
            <PanelRow label="Skiftläge">
              <TextCaseToggle
                value={object.style?.textTransform}
                disabled={locked}
                onChange={(value) => updateStyle({ textTransform: value })}
              />
            </PanelRow>
            <PanelRow label="Spärrning" stacked>
              <SliderRow
                disabled={locked}
                value={object.style?.letterSpacing ?? 0}
                min={0}
                max={14}
                step={0.5}
                startIcon={<TrackingTightIcon />}
                endIcon={<TrackingLooseIcon />}
                startTitle="Tätt"
                endTitle="Glest"
                onChange={(value) => updateStyle({ letterSpacing: value })}
              />
            </PanelRow>
            <PanelRow label="Radavstånd" stacked>
              <SliderRow
                disabled={locked}
                value={resolvedLineHeight(object.style)}
                min={0.7}
                max={2.4}
                step={0.05}
                startIcon={<LeadingTightIcon />}
                endIcon={<LeadingLooseIcon />}
                startTitle="Tätt radavstånd"
                endTitle="Glest radavstånd"
                onChange={(value) => updateStyle({ lineHeight: value })}
              />
            </PanelRow>
          </PanelSection>
          <PanelSection title="Effekter" collapsible defaultOpen={false}>
            <EffectsControls object={object} disabled={locked} onStyle={updateStyle} />
          </PanelSection>
        </Box>
      )}

      {object.type === 'table' && (
        <Box
          onMouseDown={(event) => {
            if (isToolbarPointerTarget(event.target)) return;
            event.preventDefault();
          }}
        >
          <PanelRow label="Typsnitt" stacked>
            <TypefaceSelect
              fullWidth
              disabled={locked}
              style={object.style}
              onChange={(font) => updateStyle({ fontFamily: font.family, fontWeight: font.fontWeight })}
            />
          </PanelRow>
          <PanelRow label="Storlek">
            <SteppedNumberField
              value={object.style?.fontSize || TABLE_FONT_SIZE}
              min={10}
              max={200}
              disabled={locked}
              width={72}
              onChange={(value) => updateStyle({ fontSize: value })}
            />
          </PanelRow>
          <PanelRow label="Justering">
            <TextAlignToggle
              disabled={locked}
              value={object.style?.textAlign || 'left'}
              onChange={(value) => updateStyle({ textAlign: value })}
            />
          </PanelRow>
          <PanelRow label={COPY.verticalAlign}>
            <VerticalAlignToggle
              disabled={locked}
              value={object.style?.verticalAlign || 'middle'}
              onChange={(value) => updateStyle({ verticalAlign: value })}
            />
          </PanelRow>
          <PanelSection title="Färger">
            <PanelRow label={COPY.tableHeaderFill}>
              <ColorPopoverButton
                label={COPY.tableHeaderFill}
                color={object.style?.backgroundColor || TABLE_HEADER_BG}
                disabled={locked}
                size={22}
                onChange={(color) => updateStyle({ backgroundColor: color })}
              />
            </PanelRow>
            <PanelRow label={COPY.tableHeaderText}>
              <ColorPopoverButton
                label={COPY.tableHeaderText}
                color={object.style?.color || TABLE_HEADER_COLOR}
                disabled={locked}
                size={22}
                onChange={(color) => updateStyle({ color })}
              />
            </PanelRow>
            <PanelRow label={COPY.tableGrid}>
              <ColorPopoverButton
                label={COPY.tableGrid}
                color={object.style?.strokeColor || TABLE_GRID}
                disabled={locked}
                size={22}
                onChange={(color) => updateStyle({ strokeColor: color })}
              />
            </PanelRow>
            <PanelRow label={COPY.tableStrokeWidth} stacked>
              <SliderRow
                disabled={locked}
                value={object.style?.strokeWidth ?? 1}
                min={0.5}
                max={8}
                step={0.5}
                onChange={(value) => updateStyle({ strokeWidth: value })}
              />
            </PanelRow>
          </PanelSection>
          <PanelSection title="Avancerad text" collapsible defaultOpen={false}>
            <PanelRow label="Skiftläge">
              <TextCaseToggle
                disabled={locked}
                value={object.style?.textTransform || 'none'}
                onChange={(value) => updateStyle({ textTransform: value })}
              />
            </PanelRow>
            <PanelRow label="Spärrning" stacked>
              <SliderRow
                disabled={locked}
                value={object.style?.letterSpacing ?? 0}
                min={0}
                max={14}
                step={0.5}
                startIcon={<TrackingTightIcon />}
                endIcon={<TrackingLooseIcon />}
                startTitle="Tätt"
                endTitle="Glest"
                onChange={(value) => updateStyle({ letterSpacing: value })}
              />
            </PanelRow>
            <PanelRow label="Radavstånd" stacked>
              <SliderRow
                disabled={locked}
                value={resolvedLineHeight(object.style)}
                min={0.7}
                max={2.4}
                step={0.05}
                startIcon={<LeadingTightIcon />}
                endIcon={<LeadingLooseIcon />}
                startTitle="Tätt radavstånd"
                endTitle="Glest radavstånd"
                onChange={(value) => updateStyle({ lineHeight: value })}
              />
            </PanelRow>
          </PanelSection>
        </Box>
      )}

      {object.type === 'chart' && (() => {
        const chart = normalizeChart(object.chart);
        const updateChart = (next: typeof chart) => onChange({ chart: next });
        return (
          <Box>
            <ChartLookControls chart={chart} disabled={locked} onChange={updateChart} />
            <ChartDataSheet
              chart={chart}
              contentLocked={contentLocked}
              styleLocked={locked}
              onChange={updateChart}
            />
          </Box>
        );
      })()}

      {isShape && (
        <>
          {isFillableShape(object) && (
            <PanelSection title="Text i formen" collapsible defaultOpen={false}>
              <PanelRow label="Textstil" stacked>
                <TextStyleSelect
                  fullWidth
                  object={object}
                  disabled={locked}
                  onChange={(step) => onApplyTextStyle?.(step)}
                />
              </PanelRow>
              <PanelRow label="Textfärg">
                <ColorPopoverButton
                  label="Textfärg"
                  color={object.style?.color || (object.style?.backgroundColor === '#FFFFFF' ? '#1B3967' : '#FFFFFF')}
                  disabled={locked}
                  size={22}
                  onChange={(color) => updateStyle({ color })}
                />
              </PanelRow>
              <PanelRow label="Justering">
                <TextAlignToggle
                  disabled={locked}
                  value={object.style?.textAlign || 'center'}
                  onChange={(value) => updateStyle({ textAlign: value })}
                />
              </PanelRow>
            </PanelSection>
          )}
          {hasShapeMedia(object) && (
            <PanelSection title="Bild">
              <Button
                fullWidth
                size="small"
                variant="outlined"
                onClick={onOpenAssets}
                sx={{ mb: 1, textTransform: 'none', borderColor: PRES.borderStrong, color: PRES.textPrimary }}
              >
                {COPY.replaceImage}
              </Button>
              <Button
                fullWidth
                size="small"
                variant="text"
                onClick={() => {
                  const next = clearedShapeMedia(object);
                  onChange({ assetId: next.assetId, assetVersionId: next.assetVersionId, imageUrl: next.imageUrl, style: next.style });
                }}
                sx={{ textTransform: 'none', color: PRES.textSecondary }}
              >
                {COPY.removeShapeImage}
              </Button>
            </PanelSection>
          )}
          <PanelSection title="Färger">
            {!isLine && (
              <PanelRow label="Fyllnad">
                <ColorPopoverButton
                  label="Fyllnad"
                  color={object.style?.backgroundColor || '#1B3967'}
                  allowNone
                  disabled={locked}
                  size={22}
                  onChange={(color) => updateStyle({ backgroundColor: color })}
                />
              </PanelRow>
            )}
            <PanelRow label={isLine ? 'Linjefärg' : 'Kontur'}>
              <ColorPopoverButton
                label={isLine ? 'Linjefärg' : 'Kontur'}
                color={object.style?.strokeColor || '#1B3967'}
                disabled={locked}
                size={22}
                onChange={(color) => updateStyle({ strokeColor: color })}
              />
            </PanelRow>
          </PanelSection>
          <PanelSection title="Linje">
            <PanelRow label="Tjocklek" stacked>
              <SliderRow
                disabled={locked}
                value={object.style?.strokeWidth ?? (isLine ? 3 : 0)}
                min={0}
                max={40}
                onChange={(value) => updateStyle({ strokeWidth: value })}
              />
            </PanelRow>
            <PanelRow label="Stil">
              <LineDashToggle
                disabled={locked}
                value={object.style?.strokeDash || 'solid'}
                onChange={(value) => updateStyle({ strokeDash: value })}
              />
            </PanelRow>
          </PanelSection>
          <PanelSection title="Effekter" collapsible defaultOpen={false}>
            <EffectsControls object={object} disabled={locked} onStyle={updateStyle} />
          </PanelSection>
        </>
      )}

      {(object.type === 'image' || object.type === 'product') && (
        <>
          {object.globalComponentId && !object.detached && canDetachGlobal && onDetachGlobal && (
            <Button fullWidth variant="outlined" onClick={onDetachGlobal} sx={{ mb: 1.5, textTransform: 'none' }}>
              {COPY.detachGlobal}
            </Button>
          )}
          {object.type === 'product' && onSetLinkedField && (object.linkedFields || []).map((field) => (
            <PanelRow key={field.key} label={fieldLabel(field.key, catalogLabels, logicalFields)} stacked>
              <LinkedSourceToggle
                source={field.source}
                onChange={(source) => onSetLinkedField(field.key, source)}
              />
            </PanelRow>
          ))}
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={onOpenAssets}
            sx={{ mb: 1.25, textTransform: 'none', borderColor: PRES.borderStrong, color: PRES.textPrimary }}
          >
            {COPY.replaceImage}
          </Button>
          <PanelRow label={object.trackingMode === 'trackLatest' ? COPY.followLatest : COPY.keepVersion} stacked>
            <FormControlLabel
              sx={{ m: 0, alignItems: 'flex-start' }}
              control={
                <Switch
                  checked={object.trackingMode === 'trackLatest'}
                  onChange={(event) => onChange({ trackingMode: event.target.checked ? 'trackLatest' : 'pin' })}
                />
              }
              label={
                <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary }}>
                  {object.trackingMode === 'trackLatest' ? COPY.followLatestHelp : COPY.keepVersionHelp}
                </Typography>
              }
            />
          </PanelRow>
          <PanelSection title="Effekter" collapsible defaultOpen={false}>
            <EffectsControls object={object} disabled={locked} onStyle={updateStyle} />
          </PanelSection>
        </>
      )}

      {object.conversionState && object.conversionState !== 'native' && (
        <Typography sx={{ display: 'block', mt: 1, fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary }}>
          {object.conversionState === 'raster' ? COPY.importedRaster : COPY.importedPartial}
        </Typography>
      )}

      {advancedMode && (
        <PanelSection title="Lås">
          {(['position', 'size', 'delete', 'content', 'style', 'crop', 'animation'] as Array<keyof ObjectLocks>).map((key) => (
            <PanelRow
              key={key}
              label={{
                position: 'Position',
                size: 'Storlek',
                delete: 'Radering',
                content: 'Innehåll',
                style: 'Stil',
                crop: 'Beskärning',
                animation: 'Animation',
              }[key]}
            >
              <Switch size="small" checked={Boolean(object.locks[key])} onChange={(event) => updateLock(key, event.target.checked)} />
            </PanelRow>
          ))}
          <PanelRow label="Z-index">
            <SteppedNumberField
              value={object.z}
              min={0}
              max={9999}
              onChange={(value) => onChange({ z: value })}
              width={72}
            />
          </PanelRow>
        </PanelSection>
      )}
    </Box>
  );
};

export default PropertiesPanel;
